/* The left per-section work-code legend is removed from the DOM for now (kept in
 * code as LegendContent + legendHeight so it can become a toggle later). Flip
 * this one flag to bring it back; it drives the column width + the padding rows.
 */
export const LEGEND_VISIBLE = false;

/* With the legend hidden, sections size to their real content (no padding rows);
 * with it shown, pad short sections so the legend has room to read. */
export const MIN_BODY_ROWS = LEGEND_VISIBLE ? 3 : 1;

/* ---- Continuous zoom ---- */
/* The board zooms on a single continuous lever: pixels-per-day. Everything
 * visual (row height, header cadence, day-ticks, label fit) derives from it via
 * computeZoomConfig — one slider, one pure function, every consequence. */

export const MIN_PX = 6;
export const MAX_PX = 80;
/** Pre-measurement fallback only (used before viewportW is known). The real
 *  default the user lands on is the viewport-derived 6-week `sixWeekPx`. */
export const DEFAULT_PX = 34;

export interface ZoomConfig {
  /** Pixels per workday — the continuous zoom value. */
  colWidth: number;
  /** Left zone (takt_c) label column width. */
  zoneColWidth: number;
  /** Sticky section-legend column width. */
  legendColWidth: number;
  sectionRowTotalH: number;
  zoneHeaderTotalH: number;
  dataRowInnerH: number;
  dataRowTotalH: number;
  spacerRowH: number;
  monthHeaderH: number;
  dateHeaderH: number;
  /** Total sticky date-header height (month + week + date rows). Single source
   *  for both the scroll-to-section offset and the pinned-section banner top. */
  headerHeight: number;
  /** Show the week-number header row (only when columns are wide enough). */
  showWeekRow: boolean;
  /** Render a date label every N columns (thins out as you zoom out). */
  dateLabelStep: number;
  /** Draw the per-day tick lines inside bars (dissolve when too narrow). */
  showDayTicks: boolean;
  /** Use day-of-month-only date labels (compact) vs M/D. */
  shortDates: boolean;
  /** Rows are so short that section/zone labels won't fit their controls —
   *  hide the section action buttons and shrink the header fonts. */
  compactHeaders: boolean;
}

/** Clamp a raw px/day value into the supported range (NaN → default). */
export function clampPx(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_PX;
  return Math.min(MAX_PX, Math.max(MIN_PX, n));
}

/**
 * Piecewise-linear interpolation through ascending [x, y] stops. Used so board
 * heights vary CONTINUOUSLY with the zoom (smooth feel) instead of jumping at
 * tier thresholds — the stops keep the hand-tuned values at the anchor points.
 */
function lerp(x: number, stops: number[][]): number {
  if (x <= stops[0][0]) return stops[0][1];
  for (let i = 1; i < stops.length; i++) {
    const [x0, y0] = stops[i - 1];
    const [x1, y1] = stops[i];
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
  }
  return stops[stops.length - 1][1];
}

/**
 * Derive the full board geometry from TWO levers:
 *   colWidth — px/day along the TIME axis (bar widths, date cadence, header
 *     legibility). Floored at fit-to-width so the board fills the viewport.
 *   rowPx — the VERTICAL density lever. It can drop BELOW colWidth once the
 *     horizontal axis hits fit-to-width, so "zoom out further" keeps shrinking
 *     rows/sections after the time axis can't get any wider.
 * When called with one argument the two coincide (the coupled case).
 */
export function computeZoomConfig(colRaw: number, rowRaw: number = colRaw): ZoomConfig {
  const cw = clampPx(colRaw); // horizontal (time axis)
  const rw = clampPx(rowRaw); // vertical (row density)
  // Heights interpolate continuously with the zoom (no tier "steps") so dragging
  // the slider — including past fit-to-width — feels smooth.
  const dataRowInnerH = Math.round(
    lerp(rw, [[6, 7], [10, 9], [14, 12], [22, 16], [32, 20], [80, 24]]),
  );
  // The sticky date header is the time-axis ruler — keep it legible off the
  // horizontal lever, not the (possibly tiny) row lever.
  const monthHeaderH = Math.round(lerp(cw, [[6, 16], [22, 22], [80, 24]]));
  const dateHeaderH = Math.round(lerp(cw, [[6, 13], [22, 18], [80, 22]]));
  // Week-number row is off by default — the noisiest, least-scanned header row
  // (supers read in dates, not "W28"). Kept as a field so it can become a toggle.
  const showWeekRow = false;
  const WEEK_ROW_H = 20;
  return {
    colWidth: cw,
    zoneColWidth: cw < 14 ? 40 : 60,
    legendColWidth: LEGEND_VISIBLE ? 200 : 0,
    dataRowInnerH,
    dataRowTotalH: dataRowInnerH + 1,
    spacerRowH: dataRowInnerH,
    // Row/section heights track the VERTICAL lever so they keep compressing
    // past fit-to-width.
    zoneHeaderTotalH: Math.round(lerp(rw, [[6, 10], [10, 12], [14, 13], [22, 16], [80, 20]])),
    sectionRowTotalH: Math.round(lerp(rw, [[6, 14], [10, 17], [14, 22], [22, 26], [80, 30]])),
    monthHeaderH,
    dateHeaderH,
    headerHeight: monthHeaderH + (showWeekRow ? WEEK_ROW_H : 0) + dateHeaderH,
    showWeekRow,
    dateLabelStep: cw < 10 ? 7 : cw < 16 ? 3 : cw < 26 ? 2 : 1,
    showDayTicks: cw >= 12,
    shortDates: cw < 26,
    compactHeaders: rw < 10,
  };
}
